import { Button, Checkbox, Dropdown, Field, Link, Spinner, TextInput, TextOutput } from 'Components/Core';
import { FieldSet, ListFieldSection, PageSection } from 'Components/Layout';
import { ListDropdown } from 'Components/List';
import { actions } from 'Data/ReduxViewModels/Admin';
import { Guid } from 'Lib/Guid';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { ButtonGroup } from 'semantic-ui-react';
import { reportConfigurationTypeOptions } from '../types';

const ReportConfigurationListSectionAdmin: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filter = useSelector((state) => state.admin.searchReportConfigs.filter);
  const reportConfigurations = useSelector((state) => state.admin.searchReportConfigs.configurations as any[]);
  const [documentTypes, setDocumentTypes] = useState(filter.reportedDocumentIds);
  const [configurationType, setConfigurationType] = useState(filter.configurationType);
  const [reportTypes, setReportTypes] = useState(filter.reportIds);
  const [nameQuery, setNameQuery] = useState(filter.nameQuery);
  const [includeSystemConfigs, setIncludeSystemConfigs] = useState(filter.includeSystemConfigs);

  const getSearchResults = useCallback(() => {
    dispatch(actions.reportConfigSearchGet({}));
  }, [dispatch]);

  const refreshFilter = () => {
    getSearchResults();
  };

  const idForNewItem: string | null = useSelector((state) => state.admin.reportConfiguration.create.id);

  useEffect(() => {
    if (idForNewItem) {
      dispatch(actions.clearReportConfigurationCreateId()); //clear id before navigate
      navigate(`/admin/report-configuration/${idForNewItem}`);
    }
  }, [navigate, dispatch, idForNewItem]);

  useEffect(() => {
    dispatch(
      actions.reportConfigSearchFilterSet({
        filter: {
          configurationType,
          documentTypes,
          reportTypes,
          nameQuery,
          includeSystemConfigs,
        },
      })
    );
  }, [dispatch, configurationType, documentTypes, reportTypes, nameQuery, includeSystemConfigs]);

  const createNewReportConfiguration = () => {
    dispatch(
      actions.createReportConfiguration({
        newReportConfiguration: { Name: '', Content: '' },
      })
    );
  };

  const removeConfig = (id: Guid) => {
    dispatch(actions.deleteReportConfiguration({ id: id }));
  };

  const isLoading = useSelector((state: any) => state.admin.searchReportConfigs.searching);

  return (
    <>
      <PageSection heading="Search Configurations">
        <ListFieldSection>
          <Field
            label="Configuration Type"
            input={<Dropdown />}
            options={reportConfigurationTypeOptions}
            onChange={(e: any) => {
              var configType = e?.data?.value?.value;
              setConfigurationType(configType);
              if (configType !== 'EntityContent') {
                setReportTypes([]);
              }
            }}
          />
          <Field
            label="Document Types"
            input={<ListDropdown />}
            isMulti={true}
            onChange={(e: any) => setDocumentTypes(e.data.value.map((item: any) => item.value))}
            endpoint="list.reportableDocumentTypes"
          />
          {configurationType === 'EntityContent' ? (
            <Field
              label="Inclusion in Report"
              input={<ListDropdown />}
              isMulti={true}
              onChange={(e: any) => setReportTypes(e.data.value.map((item: any) => item.value))}
              endpoint="list.reportDocumentTypes"
            />
          ) : (
            <Field blankReplacement="" input={<TextOutput />} />
          )}
          <Field label="Name" input={<TextInput />} onChange={(e: any) => setNameQuery(e.data.value)} />
          <Field
            label="Show System Defined Documents"
            input={<Checkbox />}
            defaultValue={true}
            onChange={(e: any) => setIncludeSystemConfigs(e.data.value)}
          />
          <ButtonGroup>
            <Button onClick={refreshFilter}>Find Report Configurations</Button>
          </ButtonGroup>
        </ListFieldSection>
      </PageSection>
      <PageSection heading="Configuration Search Results">
        {isLoading ? (
          <ListFieldSection>
            <div style={{ position: 'relative', minHeight: '40px' }}>
              <Spinner />
            </div>
          </ListFieldSection>
        ) : (
          reportConfigurations.map((configuration) => (
            <ListFieldSection key={configuration.Id}>
              <Field input={<TextOutput />} value={configuration.Name} />
              <FieldSet>
                <Field
                  input={<Link />}
                  to={`/admin/report-configuration/${configuration.Id}`}
                  content={`${configuration.IsSystemConfiguration ? 'View' : 'Edit'} Configuration`}
                />
                {!configuration.IsSystemConfiguration && (
                  <Field
                    input={<Link />}
                    to="#"
                    content="Delete Configuration"
                    confirm
                    confirmText="Are you sure you want to delete this configuration?"
                    confirmButtonText="Delete Configuration"
                    confirmTitle="Delete Report Configuration"
                    onClick={() => removeConfig(configuration.Id)}
                  />
                )}
              </FieldSet>
            </ListFieldSection>
          ))
        )}
      </PageSection>
      <PageSection heading="Create New Configuration">
        <FieldSet>
          <ButtonGroup>
            <Button onClick={() => createNewReportConfiguration()}>Create New Configuration</Button>
          </ButtonGroup>
        </FieldSet>
      </PageSection>
    </>
  );
};
export default ReportConfigurationListSectionAdmin;
